import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useToasts } from 'common/toast';
import { format } from 'date-fns';
import { Dropdown, Icon, Table } from 'semantic-ui-react';

import {
  useApiAdminV1RepositoryRepositoryFilesPartialUpdateMutation,
  useApiAdminV1RepositoryRepositoryFilesRegenerateEmbeddingsCreateMutation,
  useApiAdminV1RepositoryRepositoryFilesDestroyMutation,
  useApiAdminV1RepositoryRepositoryFilesCreateUrlCreateMutation,
} from '../../../../store/generated/admin/repository-files';
import {
  ResourceCategoryRead,
  useGetResourceCategoriesQuery,
  useGetFileUploadVotesQuery,
  RepositoryFileVoteRow,
} from '../api/repositoryApi';
import { FileViewModal } from './FileViewModal';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

import { Form, Grid, Segment, Button } from 'semantic-ui-react';
import { getFileLabelFromType, getFileSizeFormatted } from '../utils/LabelUtils';

import { ControlContainer, PageHeader, PageSection, Subtitle } from 'common/styles/page';
import Tabs from 'common/components/Tabs';
import {
  StyledButton,
  StyledCancelButton,
  StyledDeleteButton,
  StyledSaveButton,
} from 'common/components/button/StyledButton';
import { FileTypes } from 'common/constants/filetypes';
import {
  ProcessingStatusIndicator,
  ProcessingStatus,
  TranscriptionStatus,
} from './ProcessingStatusIndicator';

const CategoriesField = styled(Form.Field)`
  label {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: #222;
    margin-bottom: 12px;
    display: block;
  }
`;

const CategoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid #ececec;
`;

const CategoryGroupTitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 550;
  color: #216eda;
`;

const CategoryCheckboxRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  justify-content: flex-start;
  align-items: flex-start;
`;

const CategoryCheckboxWrapper = styled.div`
  margin: 0;
  padding: 0;

  label {
    font-family: 'Poppins', sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: #333;
  }
`;

export const RepositoryFileDetails = () => {
  const { addToast } = useToasts();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();

  const fileData = state?.data;
  const [formFields, setFormFields] = React.useState({ ...fileData });

  // Update form fields when state data changes
  React.useEffect(() => {
    if (state?.data) {
      setFormFields({ ...state.data });
    }
  }, [state?.data]);

  React.useEffect(() => {
    if (fileData?.category_ids) {
      setSelectedCategoryIds(fileData.category_ids);
    }
  }, [fileData]);

  const [isFileViewOpen, setFileViewOpen] = React.useState(false);
  const [deleteModalShowing, setDeleteModalShowing] = React.useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(
    fileData?.category_ids || []
  );
  const [regenerating, setRegenerating] = useState(false);

  const [triggerDelete] = useApiAdminV1RepositoryRepositoryFilesDestroyMutation();
  const [triggerUpdate] = useApiAdminV1RepositoryRepositoryFilesPartialUpdateMutation();
  const [regenerateEmbeddings] =
    useApiAdminV1RepositoryRepositoryFilesRegenerateEmbeddingsCreateMutation();
  const [triggerCreateUrlFile] = useApiAdminV1RepositoryRepositoryFilesCreateUrlCreateMutation();

  const { data: fileVotesData, isLoading: fileVotesLoading } = useGetFileUploadVotesQuery(
    String(fileData?.id),
    {
      skip: !fileData?.id,
    }
  );

  const voteRows = useMemo(
    () => fileVotesData ?? (fileData?.votes as RepositoryFileVoteRow[] | undefined) ?? [],
    [fileVotesData, fileData?.votes]
  );

  const { data: allCategoriesData, isLoading: categoriesLoading } = useGetResourceCategoriesQuery();
  const rawCategoriesData = allCategoriesData as unknown;
  const allCategories: ResourceCategoryRead[] = Array.isArray(rawCategoriesData)
    ? (rawCategoriesData as ResourceCategoryRead[])
    : Array.isArray((rawCategoriesData as { results?: ResourceCategoryRead[] })?.results)
      ? (rawCategoriesData as { results: ResourceCategoryRead[] }).results
      : [];

  const handleCancel = useCallback(() => {
    if (pathname) {
      navigate('/settings/admin/repository-management/');
    }
  }, [navigate, pathname]);

  // Redirect to list if state is missing (e.g. page refresh)
  React.useEffect(() => {
    if (!fileData) {
      navigate('/settings/admin/repository-management', { replace: true });
    }
  }, [fileData, navigate]);

  if (!fileData) {
    return null;
  }

  const tabs = [
    'Resource',
    'AI',
    'Categories',
    'Votes',
    fileData.type !== FileTypes.URL ? 'Details' : null,
  ].filter(Boolean);

  // Spinner for AI tab if processing
  const aiTabSpinner =
    fileData.processing_status === ProcessingStatus.PROCESSING ||
    fileData.transcription_status === TranscriptionStatus.IN_PROGRESS ? (
      <Icon name="spinner" loading color="blue" style={{ marginLeft: 6 }} />
    ) : null;
  const tabExtras = [null, aiTabSpinner];
  while (tabExtras.length < tabs.length) {
    tabExtras.push(null);
  }

  const resourceTypeOptions = [
    {
      key: 'academic_journal_article',
      value: 'Academic Journal Article',
      text: 'Academic Journal Article',
    },
    { key: 'article', value: 'Article', text: 'Article' },
    { key: 'blog', value: 'Blog', text: 'Blog' },
    { key: 'book_chapter', value: 'Book Chapter', text: 'Book Chapter' },
    { key: 'business_case', value: 'Business Case', text: 'Business Case' },
    { key: 'conference_paper', value: 'Conference Paper', text: 'Conference Paper' },
    { key: 'glossary', value: 'Glossary', text: 'Glossary' },
    { key: 'guide', value: 'Guide', text: 'Guide' },
    { key: 'journal_article', value: 'Journal Article', text: 'Journal Article' },
    { key: 'podcast_episode', value: 'Podcast Episode', text: 'Podcast Episode' },
    { key: 'policy', value: 'Policy', text: 'Policy' },
    { key: 'popular_media_article', value: 'Popular Media Article', text: 'Popular Media Article' },
    { key: 'report', value: 'Report', text: 'Report' },
    { key: 'research_paper', value: 'Research Paper', text: 'Research Paper' },
    { key: 'resource', value: 'Resource', text: 'Resource' },
    { key: 'review_article', value: 'Review Article', text: 'Review Article' },
    { key: 'thesis', value: 'Thesis', text: 'Thesis' },
    { key: 'tool', value: 'Tool', text: 'Tool' },
    { key: 'video', value: 'Video', text: 'Video' },
    { key: 'other', value: 'Other', text: 'Other' },
  ];

  const audienceOptions = ['Executive', 'Org Leader', 'Team Leader', 'Member'];
  const audienceString = typeof formFields.audience === 'string' ? formFields.audience : '';
  const selectedAudience = audienceString.split(',').filter(Boolean);

  const categoryGroupLabels: Record<number, string> = {
    1: 'People & Culture',
    2: 'Leadership & Growth',
    3: 'Equity & Representation',
    4: 'Workplace Systems',
    5: 'Wellbeing & Human Skills',
    6: 'Impact & Innovation',
  };

  const groupedCategories = allCategories.reduce((acc, category) => {
    const groupKey = category.category_group ?? 0;
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(category);
    return acc;
  }, {} as Record<number, ResourceCategoryRead[]>);

  const sortedCategoryGroupKeys = Object.keys(groupedCategories)
    .map(key => Number(key))
    .sort((a, b) => a - b);

  const handleDelete = () => {
    triggerDelete({ id: fileData.id })
      .unwrap()
      .then(() => {
        addToast('Successfully deleted file information', {
          appearance: 'success',
        });
        handleCancel();
      })
      .catch(() => {
        addToast('There was an error deleting file information', {
          appearance: 'error',
        });
      });
  };

  const handleViewClick = () => {
    setFileViewOpen(true);
  };

  const handleFormSubmit = async (event: any) => {
    event.preventDefault();
    try {
      if (fileData.type === FileTypes.URL && !fileData.id) {
        await triggerCreateUrlFile({
          createUrlInput: {
            url: formFields.object_key || fileData.object_key,
            name: formFields.name || fileData.name,
            type: fileData.type,
            description: formFields.description || fileData.description,
            source: formFields.source || fileData.source,
            tags: formFields.tags || fileData.tags,
            outcomes: formFields.outcomes || fileData.outcomes,
            resource_category: formFields.resource_category || fileData.resource_category,
            category_ids: selectedCategoryIds,
            reference: formFields.reference || fileData.reference,
            audience: formFields.audience || fileData.audience,
          },
        }).unwrap();
        addToast('URL file created successfully!', { appearance: 'success' });
        navigate('/settings/admin/repository-management');
      } else {
        await triggerUpdate({
          id: fileData.id,
          patchedRepositoryFile: {
            name: formFields.name || fileData.name,
            description: formFields.description || fileData.description,
            object_key: formFields.object_key || fileData.object_key,
            audience: formFields.audience || fileData.audience,
            source: formFields.source || fileData.source,
            tags: formFields.tags || fileData.tags,
            outcomes: formFields.outcomes || fileData.outcomes,
            resource_category: formFields.resource_category || fileData.resource_category,
            category_ids: selectedCategoryIds,
            reference: formFields.reference || fileData.reference,
          },
        }).unwrap();
        addToast('File updated successfully!', { appearance: 'success' });
        navigate('/settings/admin/repository-management');
      }
    } catch (error: any) {
      addToast('Error: ' + (error?.data?.error || error?.message || 'Unknown error'), {
        appearance: 'error',
      });
    }
  };

  const handleFormFieldChange = (fieldName: string, value: string) => {
    const updatedFormFields = { ...formFields, [fieldName]: value };
    setFormFields(updatedFormFields);
  };

  const handleCategoryToggle = (categoryId: number, checked: boolean) => {
    setSelectedCategoryIds(current => {
      if (checked) {
        return [...new Set([...current, categoryId])];
      }
      return current.filter(id => id !== categoryId);
    });
  };

  const handleRegenerateEmbeddings = async () => {
    setRegenerating(true);
    try {
      await regenerateEmbeddings({ id: fileData.id }).unwrap();
      addToast('Embeddings regenerated!', { appearance: 'success' });
      // Data will be updated through state changes
    } catch (error: any) {
      addToast('Error: ' + (error?.data?.error || error?.message || 'Unknown error'), {
        appearance: 'error',
      });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <>
      <PageHeader
        title={fileData.name.length > 60 ? `${fileData.name.slice(0, 60)}...` : fileData.name}
        controls={
          <ControlContainer>
            <StyledCancelButton width="auto" onClick={handleCancel} />
            <StyledSaveButton
              width="auto"
              onClick={handleFormSubmit}
              disabled={
                !formFields.name || (fileData.type === FileTypes.URL && !formFields.object_key)
              }
            />
          </ControlContainer>
        }
      />

      <>
        <Tabs
          options={tabs}
          onSelect={(item: React.SetStateAction<number>) => setSelectedTab(item)}
          selected={selectedTab}
          borderbottom
          bordertop
          tabExtras={tabExtras}
        />
      </>

      <PageSection padtop>
        <Form onSubmit={handleFormSubmit}>
          {tabs[selectedTab] === 'Resource' && (
            <Segment>
              <Grid columns={4} stackable>
                <Grid.Row>
                  <Grid.Column>
                    <Form.Field>
                      <label>Name</label>
                      <input
                        name="name"
                        value={formFields.name || ''}
                        style={{ width: '100%', maxWidth: '300px' }}
                        onChange={e => handleFormFieldChange('name', e.target.value)}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                {fileData.type === FileTypes.URL && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Form.Field>
                        <label>URL</label>
                        <input
                          name="object_key"
                          defaultValue={fileData.object_key}
                          style={{ width: '100%' }}
                          onChange={e => handleFormFieldChange('object_key', e.target.value)}
                        />
                      </Form.Field>
                    </Grid.Column>
                  </Grid.Row>
                )}

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Form.Field>
                      <label>Description</label>
                      <Form.TextArea
                        name="description"
                        value={formFields.description || ''}
                        style={{ width: '100%', height: '70px' }}
                        onChange={e => handleFormFieldChange('description', e.target.value)}
                        readOnly={fileData.processing_status === ProcessingStatus.PROCESSING}
                        placeholder={
                          fileData.processing_status === ProcessingStatus.PROCESSING
                            ? 'AI description is being generated...'
                            : fileData.processing_status === ProcessingStatus.PENDING
                              ? 'AI description will be generated automatically...'
                              : 'Enter a description or wait for AI generation...'
                        }
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={8}>
                    <Form.Field>
                      <label>Source</label>
                      <input
                        name="source"
                        value={formFields.source || ''}
                        onChange={e => handleFormFieldChange('source', e.target.value)}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Form.Field>
                      <label>Resource Type</label>
                      <Dropdown
                        placeholder="Select a resource type"
                        fluid
                        selection
                        name="resource_category"
                        value={formFields.resource_category || ''}
                        options={resourceTypeOptions}
                        onChange={(_, data) =>
                          handleFormFieldChange('resource_category', data.value as string)
                        }
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Form.Field>
                      <label>Themes</label>
                      <input
                        name="tags"
                        value={formFields.tags || ''}
                        onChange={e => handleFormFieldChange('tags', e.target.value)}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Form.Field>
                      <label>Outcomes</label>
                      <input
                        name="outcomes"
                        value={formFields.outcomes || ''}
                        onChange={e => handleFormFieldChange('outcomes', e.target.value)}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Form.Field>
                      <label>Reference</label>
                      <Form.TextArea
                        name="reference"
                        value={formFields.reference || ''}
                        style={{ width: '100%', height: '70px' }}
                        onChange={e => handleFormFieldChange('reference', e.target.value)}
                        readOnly={fileData.processing_status === ProcessingStatus.PROCESSING}
                        placeholder={'Enter a reference'}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          )}

          {tabs[selectedTab] === 'Categories' && (
            <Segment>
              <CategoriesField>
                <Subtitle>Resource Categories</Subtitle>
                {categoriesLoading ? (
                  <div>Loading categories...</div>
                ) : (
                  <div>
                    {sortedCategoryGroupKeys.map(groupKey => (
                      <CategoryGroup key={groupKey}>
                        <CategoryGroupTitle>
                          {categoryGroupLabels[groupKey] || 'Other'}
                        </CategoryGroupTitle>
                        <CategoryCheckboxRow>
                          {groupedCategories[groupKey].map(category => (
                            <CategoryCheckboxWrapper key={category.id}>
                              <Form.Checkbox
                                label={category.name}
                                checked={selectedCategoryIds.includes(category.id)}
                                onChange={(_, data) =>
                                  handleCategoryToggle(category.id, data.checked ?? false)
                                }
                              />
                            </CategoryCheckboxWrapper>
                          ))}
                        </CategoryCheckboxRow>
                      </CategoryGroup>
                    ))}
                  </div>
                )}
              </CategoriesField>
            </Segment>
          )}

          {tabs[selectedTab] === 'AI' && (
            <Segment>
              <ProcessingStatusIndicator
                processingStatus={fileData.processing_status as ProcessingStatus}
                transcriptionStatus={fileData.transcription_status as TranscriptionStatus}
                processingError={fileData.processing_error}
                transcriptionError={fileData.transcription_error}
                fileType={fileData.type}
                fileId={fileData.id}
                retryCount={fileData.retry_count}
              />
              <Grid columns={4} stackable>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Form.Field>
                      <label>AI Overview</label>
                      <Form.TextArea
                        name="ai_overview"
                        value={formFields.ai_overview || ''}
                        style={{ width: '100%', height: '300px' }}
                        readOnly
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Button
                      primary
                      loading={regenerating}
                      disabled={regenerating}
                      style={{ marginTop: 16 }}
                      onClick={handleRegenerateEmbeddings}
                    >
                      Regenerate Embeddings
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          )}

          {tabs[selectedTab] === 'Votes' && (
            <Segment>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <div style={{ display: 'flex', gap: '18px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="thumbs up" color="green" />
                    <span style={{ fontWeight: 700 }}>{fileData.upvote_count ?? 0}</span>
                    <span>Upvotes</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="thumbs down" color="red" />
                    <span style={{ fontWeight: 700 }}>{fileData.downvote_count ?? 0}</span>
                    <span>Downvotes</span>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '1.5rem' }}>
                <Table celled padded stackable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>First Name</Table.HeaderCell>
                      <Table.HeaderCell>Last Name</Table.HeaderCell>
                      <Table.HeaderCell>Email</Table.HeaderCell>
                      <Table.HeaderCell>Vote</Table.HeaderCell>
                      <Table.HeaderCell>Comments</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {fileVotesLoading ? (
                      <Table.Row>
                        <Table.Cell colSpan={5} style={{ textAlign: 'center' }}>
                          Loading vote details...
                        </Table.Cell>
                      </Table.Row>
                    ) : voteRows.length > 0 ? (
                      voteRows.map(vote => (
                        <Table.Row key={vote.id ?? `${vote.employee_id}-${vote.vote}-${vote.created_at}`}>
                          <Table.Cell>{vote.employee_first_name || '-'}</Table.Cell>
                          <Table.Cell>{vote.employee_last_name || '-'}</Table.Cell>
                          <Table.Cell>{vote.employee_email || '-'}</Table.Cell>
                          <Table.Cell>
                            <Icon
                              name={vote.vote === 1 ? 'thumbs up' : 'thumbs down'}
                              color={vote.vote === 1 ? 'green' : 'red'}
                            />
                            {vote.vote === 1 ? 'Upvote' : 'Downvote'}
                          </Table.Cell>
                          <Table.Cell>{vote.feedback_notes || '-'}</Table.Cell>
                        </Table.Row>
                      ))
                    ) : (
                      <Table.Row>
                        <Table.Cell colSpan={5} style={{ textAlign: 'center' }}>
                          No votes available for this file.
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </div>
            </Segment>
          )}

          {tabs[selectedTab] === 'Details' && fileData.type !== FileTypes.URL && (
            <Segment>
              <Grid columns={2} stackable>
                <Grid.Row>
                  <Grid.Column>
                    <Form.Field>
                      <label>ID</label>
                      <input name="id" defaultValue={fileData.id} readOnly disabled />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field>
                      <label>Filename</label>
                      <input
                        readOnly
                        disabled
                        name="objectKey"
                        defaultValue={fileData.object_key}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Form.Field>
                      <label>Repository</label>
                      <input
                        name="repository"
                        defaultValue={fileData.repository.name}
                        readOnly
                        disabled
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field>
                      <label>File Type</label>
                      <input
                        readOnly
                        disabled
                        name="type"
                        defaultValue={getFileLabelFromType(fileData.type)}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Form.Field>
                      <label>Size</label>
                      <input
                        readOnly
                        disabled
                        name="size"
                        defaultValue={getFileSizeFormatted(fileData.size) || ''}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column></Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <Form.Field>
                      <label>Created At</label>
                      <input
                        name="created_at"
                        value={format(new Date(fileData.created_at), 'MM/dd/yyyy HH:mm a')}
                        readOnly
                        disabled
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field>
                      <label>Updated At</label>
                      <input
                        name="updated_at"
                        value={format(new Date(fileData.updated_at), 'MM/dd/yyyy HH:mm a')}
                        readOnly
                        disabled
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <Form.Field>
                      <label>Shared With</label>
                      <input
                        readOnly
                        disabled
                        name="size"
                        defaultValue={fileData.share_count || 0}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field>
                      <label>Viewed By</label>
                      <input
                        readOnly
                        disabled
                        name="size"
                        defaultValue={fileData.share_viewed_count || 0}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  alignItems: 'center',
                  marginTop: '1rem',
                }}
              >
                <StyledButton width="auto" label="View File" onClick={handleViewClick} />
                <StyledDeleteButton width="auto" onClick={() => setDeleteModalShowing(true)} />
              </div>
            </Segment>
          )}
        </Form>
      </PageSection>
      <FileViewModal fileId={fileData.id} isOpen={isFileViewOpen} setModalOpen={setFileViewOpen} />

      <DeleteConfirmationModal
        isOpen={deleteModalShowing}
        onCancel={() => {
          setDeleteModalShowing(false);
        }}
        onDelete={handleDelete}
      />
    </>
  );
};
